import { BreakpointObserver } from '@angular/cdk/layout';
import { NgTemplateOutlet } from '@angular/common';
import {
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  viewChild,
  Component,
  output,
  ElementRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';

import { RtAnalyticsModule } from '@core/analytics-legacy';
import { FollowButtonComponent } from '@core/candidate-ui';
import { TranslatePipe } from '@core/i18n-legacy';
import { AssignedLocationsInfoContainerComponent } from '@core/locations-smart-ui';
import { FixedSelectOptionsHelper } from '@core/offers-logic';
import type { ViewsOffer } from '@core/offers-types';
import { OfferStatus } from '@core/offers-types';
import { JobSchedulerInfoComponent } from '@core/offers-ui';
import {
  BadgeComponent,
  Breakpoint,
  ButtonComponent,
  ClickToCopyDirective,
  DividerComponent,
  GroupComponent,
  IconComponent,
  PlaceloaderComponent,
  RtTooltip,
} from '@core/user-interface-legacy';
import { JobsAnalytics } from '@recruitee/offers-legacy';
import {
  JobActionsDropdownContainerComponent,
  StatusesDropdownContainerComponent,
} from '@recruitee/offers-smart-ui';

import type { Counter } from './offers-footer-counters/offers-footer-counters.component';
import { OffersFooterCountersComponent } from './offers-footer-counters/offers-footer-counters.component';
import { offerCardVisibleFieldsFallback } from './types';

@Component({
  selector: 'rt-offers-list-item',
  templateUrl: './offers-list-item.component.html',
  styleUrls: ['./offers-list-item.component.less'],
  host: {
    '[class.as-placeholder]': '!isVisibleOnScreen()',
  },
  imports: [
    BadgeComponent,
    ClickToCopyDirective,
    RtTooltip,
    PlaceloaderComponent,
    DividerComponent,
    IconComponent,
    GroupComponent,
    ButtonComponent,
    RtAnalyticsModule,
    OffersFooterCountersComponent,
    StatusesDropdownContainerComponent,
    JobActionsDropdownContainerComponent,
    RouterLink,
    TranslatePipe,
    NgTemplateOutlet,
    AssignedLocationsInfoContainerComponent,
    FollowButtonComponent,
    JobSchedulerInfoComponent,
  ],
})
export class OffersListItemComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly fixedSelectOptions = inject(FixedSelectOptionsHelper);
  private readonly jobsAnalytics = inject(JobsAnalytics);

  protected readonly priorityDataMap = this.fixedSelectOptions.getPriorityOptionMap();

  public offer = input.required<ViewsOffer>();
  public detailsRouterLink = input.required<string[]>();
  public isVisibleOnScreen = input.required<boolean>();
  public followLoading = input(false);
  public counters = input.required<Counter[]>();
  public fieldsVisibilityMap = input(offerCardVisibleFieldsFallback);

  public canManageJobs = input.required<boolean>();
  public canViewAcquisition = input.required<boolean>();

  public offerToggleFollow = output<{
    followed: boolean;
    id: number;
    slug: string;
  }>();

  public departmentRef = viewChild('departmentRef', { read: ElementRef });

  protected shortenItemVersion = signal(false);
  protected departmentLabelTooltipEnabled = signal(false);

  protected hasEdit = computed(
    () => this.canManageJobs() && this.offer().status !== OfferStatus.Archived,
  );

  protected hasPromote = computed(
    () =>
      this.canViewAcquisition() &&
      (this.offer().status === OfferStatus.Published ||
        this.offer().status === OfferStatus.Internal),
  );

  protected hasElementsBeforePriority = computed(
    () =>
      (this.fieldsVisibilityMap().department && this.offer().department) ||
      (this.fieldsVisibilityMap().location && this.locationIds().length) ||
      ((this.offer().onSite || this.offer().hybrid || this.offer().remote) &&
        this.fieldsVisibilityMap().work_model),
  );

  protected locationIds = computed(
    () => this.offer().locations?.map(location => location.id) ?? [],
  );

  protected departmentRefEffect = effect(() => {
    const departmentRef = this.departmentRef();
    if (departmentRef) {
      this.departmentLabelTooltipEnabled.set(
        departmentRef.nativeElement.offsetWidth < departmentRef.nativeElement.scrollWidth,
      );
    }
  });

  protected ngOnInit(): void {
    this.subscribeToBreakpointObserver();
  }

  protected editOffer() {
    const offer = this.offer();
    const status = offer.status === OfferStatus.Published ? 'public' : offer.status;
    this.jobsAnalytics.trackEdit(status);
    this.router.navigate([`/offers/${offer.slug}/edit`]);
  }

  protected followClick(): void {
    this.offerToggleFollow.emit(this.offer());
  }

  private subscribeToBreakpointObserver(): void {
    this.breakpointObserver
      .observe([Breakpoint.DESKTOP_MD_DOWN])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        this.shortenItemVersion.set(result.breakpoints[Breakpoint.DESKTOP_MD_DOWN]);
      });
  }
}
